import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, AfterViewInit, OnDestroy, input, inject } from '@angular/core';
import { ReactiveFormsModule, FormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ToastMessageService } from '@fitness-force/api';
import { AppErrors, PdfType } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import { DataGridComponent, EmptyStateContainerComponent } from '@fitness-force/grid';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { CurrencyComponent } from '@fitness-force/grid-utility/currency';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from '@fitness-force/state';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RetailProduct, RetailProductList, MarkAsFulfilledRequest } from '../../models/retail-product.model';
import { RetailProductService } from '../../services/retail-product.service';
import { NumberPrefixComponent } from '@fitness-force/common-number-prefix';
import { OrderStatusComponent } from '../retail-order-status/order-status.component';
import { StaffNameComponent } from '../staff-name/staff-name.component';


@Component({
  selector: 'lib-retail-products-tab',
  templateUrl: './retail-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DividerModule,
    TimelineModule,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    SelectModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    DialogModule,
    DataGridComponent,
    ErrorMsgComponent,
    SkeletonModule,
    InputTextModule,
    TranslateModule,
    AsyncPipe,
    DecimalPipe,
    NgClass
],
})
export class RetailProductsComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly retailProductService = inject(RetailProductService);
  private readonly cd = inject(ChangeDetectorRef);


  readonly clientId = input(0);
  readonly tenantId = input(0);
  readonly showTableActions = input(true);
  readonly showHeader = input(true);

  retailProductsData$ = new BehaviorSubject<any>([]);
  retailProductsTableColumns$ = new BehaviorSubject<any>([]);
  selectAllRetailProducts = false;

  accessedTenants: any;
  btnLoading = false;
  emptyStateComponent$ = new BehaviorSubject<any>(EmptyStateContainerComponent);
  showBulkActionBar = false;
  selectedProduct: RetailProduct | null = null;
  showFulfillmentModal = false;
  fulfillmentForm: UntypedFormGroup = new UntypedFormGroup({});
  loading = false;
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.store.select(TENANTS_INFO)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant: TENANTSINTERFACE) => {
        this.accessedTenants = tenant.accessed_tenant;
        if(tenant.selected_tenant.id > 0) {
          this.store.dispatch({ type: 'ROUTER_GO' });
          this.getRetailProducts();
        }
      });
  }

  ngAfterViewInit() {
    this.fulfillmentForm = this.fb.group({
      fulfilled_qty: [1, [Validators.required, Validators.min(1)]],
    });
  }

  getRetailProducts() {
    const clientId = this.clientId();
    this.loading = true;
    this.retailProductService.getRetailProducts(clientId).subscribe({
      next: (data: RetailProductList) => {
        this.setRetailProducts(data.items ? data.items : []);
        this.loading = false;
      },
      error: () => {
        this.setRetailProducts([]);
        this.loading = false;
      },
    });
  }

  setRetailProducts(products: RetailProduct[]) {
    products.forEach((product: RetailProduct) => {
      product.client_id = this.clientId();
    });
    this.retailProductsData$.next(products);
    this.selectAllRetailProducts = false;
    this.getRetailProductsTableColumns();
  }

  getRetailProductsTableColumns() {
    this.translate
      .get([
        'RETAIL_PRODUCTS.COLUMNS.DATE',
        'RETAIL_PRODUCTS.COLUMNS.PRODUCT',
        'RETAIL_PRODUCTS.COLUMNS.SKU',
        'RETAIL_PRODUCTS.COLUMNS.AMOUNT',
        'RETAIL_PRODUCTS.COLUMNS.INVOICE',
        'RETAIL_PRODUCTS.COLUMNS.SOLD_BY',
        'RETAIL_PRODUCTS.COLUMNS.STATUS'
      ])
      .subscribe((translations) => {
        this.columnsRetailProductsTableTranslation(translations);
      });
  }

  columnsRetailProductsTableTranslation(translations: any) {
    const columns = {
      DATE: translations['RETAIL_PRODUCTS.COLUMNS.DATE'],
      PRODUCT: translations['RETAIL_PRODUCTS.COLUMNS.PRODUCT'],
      SKU: translations['RETAIL_PRODUCTS.COLUMNS.SKU'],
      AMOUNT: translations['RETAIL_PRODUCTS.COLUMNS.AMOUNT'],
      INVOICE: translations['RETAIL_PRODUCTS.COLUMNS.INVOICE'],
      SOLD_BY: translations['RETAIL_PRODUCTS.COLUMNS.SOLD_BY'],
      STATUS: translations['RETAIL_PRODUCTS.COLUMNS.STATUS']
    };

    this.retailProductsTableColumns$.next([
      {
        title: columns.DATE,
        classes: 'py-3 text-600 w-12rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'invoice_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'invoice_date', format: 'd MMM y' },
        },
      },
      {
        title: columns.PRODUCT,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'prod_name_pri_lang',
        type: 'default',
      },
      {
        title: columns.SKU,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'sku_code',
        type: 'default',
      },
      {
        title: columns.AMOUNT,
        classes: 'py-3 text-600 w-10rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'unit_price_minor',
        type: 'component',
        data: {
          name: CurrencyComponent,
          fetch_key: 'unit_price_minor',
        },
      },
      {
        title: columns.INVOICE,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'invoice_prefix',
        type: 'component',
        data: {
          name: NumberPrefixComponent,
          additionData: true,
          aData: {
            fetch_key: 'invoice_prefix',
            type: PdfType.Invoice,
            id: 'invoice_id',
          },
        },
      },
      {
        title: columns.SOLD_BY,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'sold_by',
        type: 'component',
        data: { name: StaffNameComponent },
      },
      {
        title: columns.STATUS,
        classes: 'py-3 text-600 w-10rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'order_status',
        type: 'component',
        data: {
          name: OrderStatusComponent,
          additionData: true,
          aData: {
            fetch_key: 'order_status',
          },
        },
      }
    ]);

    if (this.showTableActions()) {
      this.retailProductsTableColumns$.value.push({
        title: '',
        classes: 'py-2 white-space-nowrap w-8rem',
        dataProperty: 'actions',
        type: 'component',
        data: {
          name: ActionsComponent,
          additionData: true,
          aData: [
            'mark_fulfilled',
            'view_details'
          ],
        },
      });
    }
  }

  handleSelectAll(selection: any) {
    if (selection.checked) {
      this.showBulkActionBar = true;
    } else {
      this.showBulkActionBar = false;
    }
  }

  handleEvent(event:any) {
    // Handle grid events if needed
  }

  handleTabAction(event: {closePopup: boolean, reloadData: boolean}) {
  }

  onActionEvent(event: any) {
    switch (event.action) {
      case 'mark_fulfilled':
        {
          this.selectedProduct = event.columnData;
          this.showFulfillmentModal = true;
          this.fulfillmentForm.patchValue({
            fulfilled_qty: (this.selectedProduct?.quantity || 1) - (this.selectedProduct?.fulfilled_qty || 0)
          });
        }
        break;
      case 'view_details':
        {
          console.log('View details for product:', event.columnData);
          // Add view details logic here if needed
        }
        break;
    }
  }

  markAsFulfilled(form: UntypedFormGroup) {
    this.btnLoading = true;
    if (form.valid && this.selectedProduct?.order_item_id) {
      const request: MarkAsFulfilledRequest = {
        fulfilled_qty: form.value.fulfilled_qty
      };

      this.retailProductService.markAsFulfilled(this.clientId(), this.selectedProduct.order_item_id, request)
        .subscribe({
          next: () => {
            this.translate
              .get('RETAIL_PRODUCTS.MSG.FULFILLMENT_SUCCESS')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.fulfillmentForm.reset();
            this.btnLoading = false;
            this.showFulfillmentModal = false;
            this.getRetailProducts(); // Refresh the data
            this.cd.detectChanges();
          },
          error: (error: AppErrors) => {
            this.btnLoading = false;
            this.toastMessageService.showError(error);
            this.cd.detectChanges();
          },
        });
    } else {
      Object.keys(this.fulfillmentForm.controls).forEach((field) => {
        const control = this.fulfillmentForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.btnLoading = false;
    }
  }

  get fulfilledQtyFormControl(): UntypedFormControl {
    return this.fulfillmentForm.controls['fulfilled_qty'] as UntypedFormControl;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.showFulfillmentModal = false;
    this.selectedProduct = null;
    this.fulfillmentForm.reset();
  }

  
}
